import { create } from "zustand";
import {
  hasSeenWhatsNew,
  markWhatsNewSeen,
  type VisitFocus,
} from "@/lib/whats-new";

// A "Try it" that opens part of a visit is handed to the visit page here,
// and taken once. It expires, so a visit that never opened (no visits, an
// error) does not open its assistant later by surprise.
const INTENT_TTL_MS = 15_000;

interface VisitIntent {
  focus: VisitFocus;
  at: number;
}

interface WhatsNewState {
  open: boolean;
  // Bumped when "seen" changes, so the menu dot re-reads it.
  seenVersion: number;
  // The first-time welcome (or its after-recording step) is on screen; the
  // announcement waits until it closes.
  welcomeOpen: boolean;
  intent: VisitIntent | null;
  show: () => void;
  hide: () => void;
  markSeen: (account: string | undefined) => void;
  isSeen: (account: string | undefined) => boolean;
  setWelcomeOpen: (open: boolean) => void;
  setIntent: (focus: VisitFocus) => void;
  // Returns true (and clears it) when a fresh intent for this focus waits.
  takeIntent: (focus: VisitFocus) => boolean;
}

export const useWhatsNewStore = create<WhatsNewState>()((set, get) => ({
  open: false,
  seenVersion: 0,
  welcomeOpen: false,
  intent: null,
  show: (): void => {
    set({ open: true });
  },
  hide: (): void => {
    set({ open: false });
  },
  markSeen: (account): void => {
    if (account === undefined) return;
    if (!hasSeenWhatsNew(account)) markWhatsNewSeen(account);
    set((state) => ({ seenVersion: state.seenVersion + 1 }));
  },
  isSeen: (account): boolean =>
    account !== undefined && hasSeenWhatsNew(account),
  setWelcomeOpen: (open): void => {
    if (get().welcomeOpen !== open) set({ welcomeOpen: open });
  },
  setIntent: (focus): void => {
    set({ intent: { focus, at: Date.now() } });
  },
  takeIntent: (focus): boolean => {
    const { intent } = get();
    if (!intent || intent.focus !== focus) return false;
    set({ intent: null });
    return Date.now() - intent.at <= INTENT_TTL_MS;
  },
}));
